import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState } from 'react';
import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import CssBaseline from '@mui/material/CssBaseline';
import TextField from '@mui/material/TextField';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Link from '@mui/material/Link';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import Typography from '@mui/material/Typography';
import Container from '@mui/material/Container';
import { ThemeProvider } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import theme from './theme';
import { useTranslation } from 'react-i18next';
import i18next from 'i18next';
import { getAuth, createUserWithEmailAndPassword, updateProfile, sendEmailVerification } from 'firebase/auth';
import { getFirestore, doc, setDoc } from 'firebase/firestore';
import axiosInstance, { getApiBaseUrl } from '../components/services/axiosInstance.js';

const SignUp = () => {
    const { t } = useTranslation();
    const [error, setError] = useState(null);
    const [formData, setFormData] = useState({
        username: '',
        email: '',
        password: '',
        consent: false,
    });
    const navigate = useNavigate();

    const handleChange = (event) => {
        const { name, value, type, checked } = event.target;
        setFormData((prevData) => ({
            ...prevData,
            [name]: type === 'checkbox' ? checked : value,
        }));
    };

    const validateForm = () => {
        const { username, email, password, consent } = formData;
        if (!username || !email || !password || !consent) {
            return t('form_incomplete');
        }
        if (password.length < 6) {
            return t('password_too_short');
        }
        return null;
    };

    // Function to sync user data with backend (Flask API)
    const syncUserWithBackend = async (user) => {
        try {
            const idToken = await user.getIdToken(); // Get Firebase ID token
        
            // Add additional required fields
            const currentDate = new Date().toISOString(); // ISO format timestamp
            const userData = {
                id_token: idToken,
                email: user.email,
                role: 'user', // or assign role dynamically later
                created_at: currentDate,
                updated_at: currentDate,
                signed_in: true, // User is signed in
            };
    
            // Use centralized API instance
            const response = await axiosInstance.post('/api/sync_users/', userData);
        
            console.log("✅ User synced:", response.data.message);
        } catch (error) {
            console.error("❌ Failed to sync user:", error.response?.data || error.message);
        }
    };
    
    const handleSubmit = async (event) => {
        event.preventDefault();
        console.log('Form submission triggered');
        
        const validationError = validateForm();
        if (validationError) {
            console.log('Validation error:', validationError);
            setError(validationError);
            return;
        }

        const { email, password, username } = formData;
        console.log('Attempting registration for:', email);

        try {
            const auth = getAuth();

            // Set Firebase language to the user's selected language
            auth.languageCode = i18next.language; // Dynamically set language (e.g., "en", "fr", "de")

            // Create the user with email and password
            const userCredential = await createUserWithEmailAndPassword(auth, email, password);
            const user = userCredential.user;
            console.log('User created:', user);

            // Update the profile with the username
            await updateProfile(user, { displayName: username });
            console.log('Profile updated with username:', username);

            // Save user data to Firestore
            const db = getFirestore();
            await setDoc(doc(db, 'users', user.uid), {
                username,
                email,
                role: '', // Placeholder; role will be set in RoleSelection
            });
            console.log('User saved to Firestore with UID:', user.uid);

            // Send verification email after successful registration
            await sendEmailVerification(user);
            console.log('Verification email sent');

            // Sync user data to backend MySQL database (including email)
            await syncUserWithBackend(user);

            // Redirect to the role selection page after successful registration
            navigate('/role-selection');
        } catch (error) {
            console.error('Firebase error:', error);
            let errorMessage = t('api_error');
            if (error.code) {
                errorMessage = t(`firebase_auth_errors.${error.code}`) || t('api_error');
            }
            setError(errorMessage);
        }
    };

    return (_jsx(ThemeProvider, { theme: theme, children: _jsxs(Container, { component: "main", maxWidth: "xs", children: [_jsx(CssBaseline, {}), _jsxs(Box, { sx: {
                        marginTop: 8,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                    }, children: [_jsx(Avatar, { sx: { m: 1, bgcolor: 'secondary.main' }, children: _jsx(LockOutlinedIcon, {}) }), _jsx(Typography, { component: "h1", variant: "h5", children: t('sign_up') }), _jsxs(Box, { component: "form", noValidate: true, onSubmit: handleSubmit, sx: { mt: 1 }, children: [_jsxs(Grid, { container: true, spacing: 2, children: [_jsx(Grid, { item: true, xs: 12, children: _jsx(TextField, { fullWidth: true, name: "username", label: t('username'), value: formData.username, onChange: handleChange }) }), _jsx(Grid, { item: true, xs: 12, children: _jsx(TextField, { fullWidth: true, name: "email", label: t('email_address'), value: formData.email, onChange: handleChange }) }), _jsx(Grid, { item: true, xs: 12, children: _jsx(TextField, { fullWidth: true, name: "password", type: "password", label: t('password'), value: formData.password, onChange: handleChange }) }), _jsx(Grid, { item: true, xs: 12, children: _jsx(FormControlLabel, { control: _jsx(Checkbox, { name: "consent", color: "primary", checked: formData.consent, onChange: handleChange }), label: t('receive_emails') }) })] }), error && (_jsx(Typography, { variant: "body2", color: "error", align: "center", sx: { mt: 1 }, children: error })), _jsx(Button, { type: "submit", fullWidth: true, variant: "contained", sx: { mt: 3, mb: 2 }, children: t('sign_up') }), _jsx(Grid, { container: true, justifyContent: "flex-end", children: _jsx(Grid, { item: true, children: _jsx(Link, { href: "/signin", variant: "body2", children: t('already_have_account') }) }) })] })] })] }) } ));
};

export default SignUp;
